import React, { useEffect, useMemo, useState } from "react";
import { Table, Tag, Button } from "antd";
import { ColumnsType, TableProps } from "antd/es/table";
import dayjs from "dayjs";
import { Booking, bookingStatusLabels, BookingStatus } from "../../types/booking";
import { statusColors } from "../../utils/bookingStatus";
import { AGENT_BOOKING_STATUS_TABS, AGENT_LIST_BOOKING_STATUSES } from "../../config/agentPortal";
import { AGENT_TABLE_SCROLL_X } from "../../config/agentPortalLayout";
import { filterBookingsBySearchText } from "../../utils/agentBookingListFilters";
import { getBookingEffReference } from "../../utils/bookingUtils";
import {
  shouldShowGoodsReadyColumn,
  shouldShowGoodsReadyForBooking,
} from "../../utils/agentBookingGoodsReadyColumn";
import {
  formatBookingVolumeColumn,
  getBookingTypeLabel,
  getBookingTypeTagStyle,
} from "../../utils/agentBookingMetrics";
import {
  comparePickupUrgency,
  matchesPickupDateFilter,
  sortBookingsByPickupDate,
  AgentPickupDateFilter,
} from "../../utils/agentBookingPickupDate";
import {
  AgentBookingsFilters,
  AgentHasContainerFilter,
} from "./AgentBookingsFilters";
import { AgentContainerOption } from "../../services/agentPortalService";
import { AgentPickupDateCell } from "./AgentPickupDateCell";
import { AgentCiValidityCell } from "./AgentCiValidityCell";
import { AgentListTabs } from "./AgentListTabs";
import {
  isServiceLevel,
  SERVICE_LEVEL_LABELS,
  type ServiceLevel,
} from "../../utils/serviceLevelUtils";

type SearchMode = "none" | "id";
type PickupSortOrder = "ascend" | "descend" | null;

interface AgentBookingsListProps {
  bookings: Booking[];
  loading: boolean;
  statusFilter: string;
  search: string;
  searchMode: SearchMode;
  listTruncated?: boolean;
  companyId?: number;
  transportTypeId?: number;
  hasContainer?: AgentHasContainerFilter;
  containerId?: number;
  masterContainerId?: number;
  bookingTypeId?: number;
  serviceLevel?: ServiceLevel;
  pickupFilter?: AgentPickupDateFilter;
  companies: { id: number; name: string }[];
  transportTypes: { id: number; name: string }[];
  bookingTypes: { id: number; name: string }[];
  containers: AgentContainerOption[];
  masters: { id: number; reference: string | null }[];
  loadingOptions: boolean;
  hasActiveFilters: boolean;
  statusCounts: Partial<
    Record<(typeof AGENT_LIST_BOOKING_STATUSES)[number], number>
  >;
  onStatusChange: (status: string) => void;
  onSearchChange: (value: string) => void;
  onCompanyChange: (value?: number) => void;
  onTransportChange: (value?: number) => void;
  onHasContainerChange: (value?: AgentHasContainerFilter) => void;
  onContainerChange: (value?: number) => void;
  onMasterContainerChange: (value?: number) => void;
  onBookingTypeChange: (value?: number) => void;
  onServiceLevelChange: (value?: ServiceLevel) => void;
  onPickupFilterChange: (value?: AgentPickupDateFilter) => void;
  onClearFilters: () => void;
  onRefresh: () => void;
  onOpenBooking: (bookingId: number) => void;
  selectedRowKeys: React.Key[];
  onSelectionChange: (keys: React.Key[]) => void;
}

const normalizeStatus = (status: string | null) =>
  (status || "").replace(/-/g, "_");

const getAllTabCount = (
  statusCounts: Partial<
    Record<(typeof AGENT_LIST_BOOKING_STATUSES)[number], number>
  >
): number =>
  AGENT_LIST_BOOKING_STATUSES.reduce(
    (sum, status) => sum + (statusCounts[status] ?? 0),
    0
  );

export const AgentBookingsList: React.FC<AgentBookingsListProps> = ({
  bookings,
  loading,
  statusFilter,
  search,
  searchMode,
  listTruncated,
  companyId,
  transportTypeId,
  hasContainer,
  containerId,
  masterContainerId,
  bookingTypeId,
  serviceLevel,
  pickupFilter,
  companies,
  transportTypes,
  bookingTypes,
  containers,
  masters,
  loadingOptions,
  hasActiveFilters,
  statusCounts,
  onStatusChange,
  onSearchChange,
  onCompanyChange,
  onTransportChange,
  onHasContainerChange,
  onContainerChange,
  onMasterContainerChange,
  onBookingTypeChange,
  onServiceLevelChange,
  onPickupFilterChange,
  onClearFilters,
  onRefresh,
  onOpenBooking,
  selectedRowKeys,
  onSelectionChange,
}) => {
  const [pickupSortOrder, setPickupSortOrder] = useState<PickupSortOrder>(null);

  useEffect(() => {
    if (pickupFilter) {
      setPickupSortOrder("ascend");
    }
  }, [pickupFilter]);

  const filtered = useMemo(() => {
    let rows =
      searchMode === "id" ? bookings : filterBookingsBySearchText(bookings, search);

    if (pickupFilter) {
      rows = rows.filter((b) => matchesPickupDateFilter(b, pickupFilter));
    }

    if (pickupSortOrder) {
      rows = sortBookingsByPickupDate(rows, pickupSortOrder);
    }

    return rows;
  }, [bookings, search, searchMode, pickupFilter, pickupSortOrder]);

  const handleTableChange: TableProps<Booking>["onChange"] = (
    _pagination,
    _filters,
    sorter
  ) => {
    const pickupSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    if (pickupSorter?.columnKey === "origin_pickup_date") {
      setPickupSortOrder(
        pickupSorter.order === "ascend" || pickupSorter.order === "descend"
          ? pickupSorter.order
          : null
      );
    }
  };

  const showGoodsReadyColumn = shouldShowGoodsReadyColumn(statusFilter);

  const columns: ColumnsType<Booking> = [
    {
      title: "Booking",
      key: "booking_ref",
      width: 130,
      fixed: "left",
      sorter: (a, b) => a.id - b.id,
      render: (_, row) => getBookingEffReference(row),
    },
    {
      title: "Created",
      dataIndex: "created_at",
      width: 120,
      render: (v: string) => dayjs(v).format("MMM D, YYYY"),
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 160,
      render: (status: string) => {
        const key = normalizeStatus(status) as BookingStatus;
        return (
          <Tag color={statusColors[key] || "default"}>
            {bookingStatusLabels[key] || status}
          </Tag>
        );
      },
    },
    ...(showGoodsReadyColumn
      ? [
          {
            title: "Goods ready",
            key: "origin_pickup_date",
            width: 130,
            sorter: (a: Booking, b: Booking) => comparePickupUrgency(a, b),
            sortOrder: pickupSortOrder ?? undefined,
            sortDirections: ["ascend", "descend"] as const,
            render: (_: unknown, row: Booking) =>
              shouldShowGoodsReadyForBooking(row) ? (
                <AgentPickupDateCell booking={row} />
              ) : (
                "-"
              ),
          },
        ]
      : []),
    {
      title: "CI",
      key: "ci_validity",
      width: 64,
      align: "center",
      sorter: (a, b) => {
        const score = (row: Booking) => {
          const path = row.commercial_invoice_file?.split(",")[0]?.trim();
          if (!path) return -1;
          const analysis = Array.isArray(row.commercial_invoice_analysis)
            ? row.commercial_invoice_analysis[0]
            : row.commercial_invoice_analysis;
          if (!analysis || analysis.object_path !== path) return -1;
          return analysis.likelihood_score;
        };
        return score(a) - score(b);
      },
      render: (_, row) => <AgentCiValidityCell booking={row} />,
    },
    {
      title: "Company",
      key: "company",
      ellipsis: true,
      render: (_, row) => {
        const company = Array.isArray(row.company_info)
          ? row.company_info[0]
          : row.company_info;
        return company?.name || "-";
      },
    },
    {
      title: "LCL/FCL",
      key: "booking_type",
      width: 90,
      render: (_, row) => {
        const label = getBookingTypeLabel(row);
        if (label === "-") return "-";
        const tagStyle = getBookingTypeTagStyle(label);
        if (!tagStyle) return label;
        return <Tag style={tagStyle}>{label}</Tag>;
      },
    },
    {
      title: "Service",
      key: "service_level",
      width: 100,
      render: (_, row) => {
        if (row.service_level && isServiceLevel(row.service_level)) {
          return (
            <Tag color={row.service_level === "premium" ? "gold" : "default"}>
              {SERVICE_LEVEL_LABELS[row.service_level]}
            </Tag>
          );
        }
        return "-";
      },
    },
    {
      title: "Transport",
      key: "transport",
      width: 100,
      render: (_, row) => {
        const t = Array.isArray(row.transport_type_info)
          ? row.transport_type_info[0]
          : row.transport_type_info;
        return t?.name || "-";
      },
    },
    {
      title: "CBM / KG",
      key: "volume",
      width: 100,
      render: (_, row) => formatBookingVolumeColumn(row),
    },
    {
      title: "Container",
      key: "container",
      width: 130,
      render: (_, row) => {
        const c = Array.isArray(row.container_info)
          ? row.container_info[0]
          : row.container_info;
        return c?.reference || "-";
      },
    },
    {
      title: "",
      key: "actions",
      width: 90,
      render: (_, row) => (
        <Button type="link" size="small" onClick={() => onOpenBooking(row.id)}>
          Open
        </Button>
      ),
    },
  ];

  const hasCounts = AGENT_LIST_BOOKING_STATUSES.some(
    (s) => statusCounts[s] != null
  );

  return (
    <>
      <AgentListTabs
        activeKey={statusFilter}
        onChange={onStatusChange}
        items={AGENT_BOOKING_STATUS_TABS.map((tab) => ({
          key: tab.key,
          label: tab.label,
          count:
            tab.key === "all"
              ? hasCounts
                ? getAllTabCount(statusCounts)
                : undefined
              : statusCounts[
                  tab.key as (typeof AGENT_LIST_BOOKING_STATUSES)[number]
                ],
        }))}
      />
      <AgentBookingsFilters
        search={search}
        companyId={companyId}
        transportTypeId={transportTypeId}
        hasContainer={hasContainer}
        containerId={containerId}
        masterContainerId={masterContainerId}
        bookingTypeId={bookingTypeId}
        serviceLevel={serviceLevel}
        pickupFilter={pickupFilter}
        companies={companies}
        transportTypes={transportTypes}
        bookingTypes={bookingTypes}
        containers={containers}
        masters={masters}
        loadingOptions={loadingOptions}
        hasActiveFilters={hasActiveFilters}
        resultCount={filtered.length}
        statusFilter={statusFilter}
        searchMode={searchMode}
        listTruncated={listTruncated && !pickupFilter}
        pickupSortActive={!!pickupFilter && pickupSortOrder === "ascend"}
        onSearchChange={onSearchChange}
        onCompanyChange={onCompanyChange}
        onTransportChange={onTransportChange}
        onHasContainerChange={onHasContainerChange}
        onContainerChange={onContainerChange}
        onMasterContainerChange={onMasterContainerChange}
        onBookingTypeChange={onBookingTypeChange}
        onServiceLevelChange={onServiceLevelChange}
        onPickupFilterChange={onPickupFilterChange}
        onClearFilters={onClearFilters}
        onRefresh={onRefresh}
      />
      <Table
        rowKey="id"
        size="small"
        loading={loading}
        columns={columns}
        dataSource={filtered}
        scroll={{ x: AGENT_TABLE_SCROLL_X }}
        onChange={handleTableChange}
        rowSelection={{
          selectedRowKeys,
          onChange: onSelectionChange,
        }}
        pagination={{
          pageSize: 100,
          showSizeChanger: true,
          responsive: true,
          showTotal: (total) => `${total} bookings`,
        }}
        onRow={(record) => ({
          onClick: (e) => {
            const target = e.target as HTMLElement;
            if (target.closest("button") || target.closest(".ant-checkbox")) return;
            onOpenBooking(record.id);
          },
          style: { cursor: "pointer" },
        })}
      />
    </>
  );
};

export { AgentConsolidationBar } from "./AgentConsolidationBar";
